<template>
  <div class="reprocesso-form">
    <select-list
      :data-testid="testIdPrefix + '-step'"
      elem-id="reprocesso-step-select"
      :label="t('Passo')"
      :itens-select="stepOptions"
      :model-value="formData.work_order_step_id"
      @update:model-value="formData.work_order_step_id = $event"
    />

    <div class="photo-section">
      <label class="section-label">{{ t('Foto Antes') }} <span class="required-mark">*</span></label>
      <small class="photo-hint">📸 {{ t('É obrigatório enviar uma foto do estado atual do item para abrir o reprocesso.') }}</small>
      <CameraCapture
        :data-testid="testIdPrefix + '-photo-before'"
        @photo-captured="handlePhotoBeforeCaptured"
      />
    </div>

    <input-text-area
      :data-testid="testIdPrefix + '-notes'"
      elem-id="reprocesso-notes"
      :label="t('Motivo')"
      :placeholder="t('Descreva o motivo do reprocesso')"
      :model-value="formData.notes"
      @update:model-value="formData.notes = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOReprocessos } from '../../composables/useSOReprocessos';
import { log, error as logError } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface CompletedStep {
  so_work_order_step_id: Id;
  so_work_order_step_name?: string;
  so_work_order_step_production_step_id?: Id;
  [key: string]: unknown;
}

interface Props {
  orderItemId: number;
  completedSteps?: CompletedStep[];
  testIdPrefix?: string;
}

const props = withDefaults(defineProps<Props>(), {
  completedSteps: () => [],
  testIdPrefix: 'so-reprocesso-form'
});

const emit = defineEmits<{
  'save-success': [];
}>();

const { t } = useI18n();
const { criar, uploadPhoto } = useSOReprocessos();

const formData = ref<{ work_order_step_id: Id | null; notes: string }>({
  work_order_step_id: props.completedSteps.length > 0 ? props.completedSteps[0].so_work_order_step_id : null,
  notes: ''
});
const photoBeforeFile = ref<File | null>(null);

const stepOptions = computed(() => {
  return props.completedSteps.map((step) => ({
    ID: step.so_work_order_step_id,
    Name: step.so_work_order_step_name
  }));
});

const canSave = computed<boolean>(() => {
  return (
    formData.value.work_order_step_id !== null &&
    formData.value.work_order_step_id !== ''
  );
});

const handlePhotoBeforeCaptured = (file: File): void => {
  log('SOReprocessoForm', 'handlePhotoBeforeCaptured', 'Before photo captured', { name: file.name, size: file.size });
  photoBeforeFile.value = file;
};

const saveReprocesso = async (): Promise<void> => {
  log('SOReprocessoForm', 'saveReprocesso', 'Saving reprocesso', formData.value);

  // Validate step selected
  if (!formData.value.work_order_step_id || formData.value.work_order_step_id === '') {
    eventBus.emit('globalMessage.error', t('Selecione uma etapa para solicitar o reprocesso.'));
    throw new Error('Etapa não selecionada');
  }

  // Validate photo
  if (!photoBeforeFile.value) {
    eventBus.emit('globalMessage.error', t('📸 É obrigatório enviar uma foto do estado atual do item para solicitar o reprocesso.'));
    throw new Error('Foto obrigatória');
  }

  try {

    let photoUrl: string;
    try {
      photoUrl = await uploadPhoto(photoBeforeFile.value) as string;
      log('SOReprocessoForm', 'saveReprocesso', 'Photo uploaded successfully', { url: photoUrl });
    } catch (photoErr: unknown) {
      logError('SOReprocessoForm', 'saveReprocesso', 'Photo upload failed', photoErr);
      const message = photoErr instanceof Error ? photoErr.message : String(photoErr);
      eventBus.emit('globalMessage.error', t('Erro ao enviar foto: ') + message);
      throw photoErr;
    }

    const selectedStep = props.completedSteps.find(
      (s) => s.so_work_order_step_id === formData.value.work_order_step_id
    );

    const payload = {
      order_item_id: props.orderItemId,
      production_step_id: Number(selectedStep?.so_work_order_step_production_step_id),
      photo_before: photoUrl,
      work_order_step_id: formData.value.work_order_step_id !== null ? Number(formData.value.work_order_step_id) : undefined,
      notes: formData.value.notes || undefined,
    };

    await criar(payload);

    log('SOReprocessoForm', 'saveReprocesso', 'Reprocesso saved successfully');
    emit('save-success');

    // Reset form
    formData.value = {
      work_order_step_id: null,
      notes: ''
    };
    photoBeforeFile.value = null;
  } catch (err: unknown) {
    logError('SOReprocessoForm', 'saveReprocesso', 'Error saving reprocesso', err);
    throw err;
  }
};

defineExpose({
  saveReprocesso,
  canSave
});
</script>

<style scoped>
.reprocesso-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.photo-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.required-mark {
  color: var(--p-red-500, #ef4444);
}

.photo-hint {
  display: block;
  margin-top: 4px;
  padding: 8px 10px;
  background: var(--p-blue-50, #eff6ff);
  border: 1px solid var(--p-blue-200, #bfdbfe);
  border-radius: 6px;
  color: var(--p-blue-700, #1d4ed8);
  font-size: 0.8rem;
  line-height: 1.4;
}
</style>
